import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import {Button} from '../button/button';
// @ts-expect-error - imported from .js/.jsx file
import {UpgradeArrowIcon} from '../icon/svg/upgrade-arrow-icon';
import {StackLegacy} from '../layout/stack/stack-legacy';
import {Link} from '../link/link';
import type {SubdomainOption} from '../subdomain-select/subdomain-select';
import {Switch} from '../switch/switch';
import {Body} from '../typography/body';

import type {PageConfig, UpdatePageConfigInput} from './types';
import {UrlCustomizer} from './url-customizer';

import styles from './meeting-customizer.module.css';

export type Props = {
    meetingPageUrl: string;
    allowedToCustomizeUrl: boolean;
    hasSchedulerFullAccess: boolean;
    hasUploadedAvatar: boolean;
    subdomainOptions: SubdomainOption[];
    refetchMeetings: () => Promise<any>;
    onUpdatePageConfig: (input: UpdatePageConfigInput) => Promise<any>;
    onOpenFeatureGateModal: () => void;
};

type OwnProps = Props & {
    pageConfig: PageConfig;
};

export function MeetingCustomizer(props: OwnProps) {
    const hasUploadedCompanyLogo = Boolean(NutClientConfig.largeLogoUrl);

    return (
        <>
            <UrlCustomizer
                meetingPageUrl={props.meetingPageUrl}
                domain={props.pageConfig.domain}
                slug={props.pageConfig.userSlug}
                allowedToCustomizeUrl={props.allowedToCustomizeUrl}
                hasSchedulerFullAccess={props.hasSchedulerFullAccess}
                subdomainOptions={props.subdomainOptions}
                refetchMeetings={props.refetchMeetings}
                onUpdatePageConfig={props.onUpdatePageConfig}
                onOpenFeatureGateModal={props.onOpenFeatureGateModal}
            />
            {props.hasSchedulerFullAccess && (
                <StackLegacy spacing={48} isHorizontal={true} alignCenter={true}>
                    <StackLegacy spacing={8} isHorizontal={true} alignCenter={true}>
                        <Switch
                            isRounded={true}
                            isOn={props.pageConfig.useCompanyLogo}
                            onChange={() => {
                                props.onUpdatePageConfig({
                                    useCompanyLogo: !props.pageConfig.useCompanyLogo,
                                });
                            }}
                            isDisabled={!hasUploadedCompanyLogo}
                        />
                        <StackLegacy spacing={0}>
                            <Body>Include logo</Body>
                            {!hasUploadedCompanyLogo && NutClientConfig.loggedInUserIsAdmin && (
                                <Link
                                    to='/setup/index'
                                    // @ts-expect-error
                                    as={RoutedLink}
                                    variant='primary'
                                    size='small'
                                >
                                    Upload logo
                                </Link>
                            )}
                        </StackLegacy>
                    </StackLegacy>
                    <StackLegacy spacing={8} isHorizontal={true} alignCenter={true}>
                        <Switch
                            isRounded={true}
                            isOn={props.pageConfig.useAvatar}
                            onChange={() => {
                                props.onUpdatePageConfig({
                                    useAvatar: !props.pageConfig.useAvatar,
                                });
                            }}
                            isDisabled={!props.hasUploadedAvatar}
                        />
                        <StackLegacy spacing={0}>
                            <Body>Include personal avatar</Body>
                            {!props.hasUploadedAvatar && (
                                <Link
                                    to='/my-account/settings'
                                    // @ts-expect-error
                                    as={RoutedLink}
                                    variant='primary'
                                    size='small'
                                >
                                    Upload avatar
                                </Link>
                            )}
                        </StackLegacy>
                    </StackLegacy>
                </StackLegacy>
            )}
            {!props.hasSchedulerFullAccess && (
                <StackLegacy spacing={8}>
                    <StackLegacy spacing={8} isHorizontal={true} alignCenter={true}>
                        <Body>Personalize your meeting page</Body>
                        <UpgradeArrowIcon size='15' alignCenter={true} />
                    </StackLegacy>
                    <div className={styles['upgrade-section']}>
                        <Body size='small' color='light'>
                            Upgrade to Nutshell Business or above to personalize your meeting page
                            link, create secret meetings, and add your company’s logo and your own
                            avatar
                        </Body>
                        <Button
                            variant='text-primary'
                            size='small'
                            onClick={props.onOpenFeatureGateModal}
                        >
                            Learn more
                        </Button>
                    </div>
                </StackLegacy>
            )}
        </>
    );
}
